import type { NavigateToOptions } from '#app/composables/router'
import type { SupportedLocales } from '#i18n/types'
import type {
  LocationQuery,
  RouteLocationNamedRaw,
  RouteLocationNormalizedGeneric,
  RouteLocationPathRaw,
  RouteLocationRaw,
} from '#vue-router'

export const useRouterUtils = () => {
  const { back, currentRoute, replace } = useRouter()
  const localePath = useLocalePath()

  const encodedRedirect = computed(() => redirectQuery.value.redirect)
  const decodedRedirect = computed(() => decodeRedirectRoute(currentRoute.value))
  const redirectQuery = computed(() => currentRoute.value.query)

  const getDecodedQueryParams = (route: RouteLocationNormalizedGeneric) => {
    const encodedQueryParams = ['redirect']

    return encodedQueryParams.reduce(
      (all, param) => {
        if (typeof param !== 'string') {
          return all
        }

        const decodedQuery: LocationQuery = {}

        try {
          const searchParams = new URLSearchParams(
            decodeURIComponent(route.query?.[param] ? String(route.query?.[param]) : ''),
          )
          searchParams.forEach((value, key) => {
            decodedQuery[key] = value
          })
        } catch (e) {
          console.error(`Failed to decode ${param} query parameter:`, e)
          return all
        }

        return R_merge(all, decodedQuery)
      },
      {} as Record<string, any>,
    )
  }

  const navigateToLocalized = (
    to: RouteLocationRaw,
    options?: NavigateToOptions,
    locale?: SupportedLocales['code'],
  ) => navigateTo(localePath(to, locale), options)

  const navigateBack = async (options?: {
    fallbackRoute?: RouteLocationRaw
    previousRoute?: RouteLocationRaw
  }) => {
    const { previousRoute } = useRoute().meta
    const to = options?.previousRoute || previousRoute

    if (to) {
      await navigateToLocalized(to, { replace: true })
    } else if (window.history.state.back) {
      back()
    } else if (options?.fallbackRoute) {
      await navigateToLocalized(options.fallbackRoute, { replace: true })
    } else {
      await navigateToLocalized('/', { replace: true })
    }
  }

  const navigateToWithRedirect = (
    to: RouteLocationNamedRaw | RouteLocationPathRaw,
    options?: {
      replace?: boolean
    },
  ) => navigateToLocalized(createRedirectLocation(to, encodedRedirect.value), options)

  const navigateToLocalizedBackroute = () => {
    const backroute = useRoute().query.backroute
    const path = typeof backroute === 'string' ? backroute : '/'
    navigateToLocalized(path, { replace: true })
  }

  const removeQueryParams = (...params: string[]) =>
    replace({
      query: R_omit(currentRoute.value.query, params),
    })

  return {
    decodedRedirect,
    encodedRedirect,
    getDecodedQueryParams,
    navigateBack,
    navigateToLocalized,
    navigateToLocalizedBackroute,
    navigateToWithRedirect,
    redirectQuery,
    removeQueryParams,
  }
}
